import { IMMUNISATION_BASE_URL } from "@qendo/medical";
import { MedicalType } from "@qendo/medical/types";
import { OptionType } from "@qendo/types";
import { Stack } from "expo-router";
import { useMemo, useState } from "react";

import { Button } from "~/components/Button";
import { FilteredList } from "~/components/layout/FilteredList";
import { ListingItem } from "~/components/layout/ListingItem";
import { Page } from "~/components/layout/Page";
import { SectionHeader } from "~/components/layout/SectionHeader";
import { Authorize } from "~/components/permission/Authorize";
import { Can } from "~/components/permission/Can";
import { Only } from "~/components/permission/Only";
import { ShareButton } from "~/components/ShareButton";
import type { Immunisation } from "~/entities/medical/immunisation";
import { useMedical } from "~/providers/MedicalProvider";
import { useOptions } from "~/providers/OptionsProvider";
import { formatDateForHumans } from "~/util/dates";

interface ImmunisationListingItem {
    id: string;
    title: string;
    date: string | null;
}

const type = MedicalType.immunisation;

export default function ImmunisationsIndex() {
    const { listMedical, refreshMedicalCache } = useMedical();
    const { findOption } = useOptions();
    const [refreshing, setRefreshing] = useState(false);

    const refresh = async () => {
        if (refreshing) {
            return;
        }

        setRefreshing(true);
        await refreshMedicalCache(type).finally(() => setRefreshing(false));
    };

    const items: ImmunisationListingItem[] = useMemo(() => {
        return listMedical(type).map((i: Immunisation) => ({
            id: i.id as string,
            title: findOption(OptionType.immunisations, i.option_id)?.title as string,
            date: i.immunisation_date ? formatDateForHumans(i.immunisation_date, "short") : null,
        }));
    }, [listMedical, findOption]);

    const has_items = items.length > 0;

    return (
        <Authorize ability="view" section="immunisations">
            <Stack.Screen
                options={{
                    headerRight: () => (
                        <ShareButton title="Share immunisations" initial={["immunisations", "photos"]} />
                    ),
                }}
            />
            <Page spacer refreshWith={refresh} isRefreshing={refreshing} scrollable={!has_items}>
                <SectionHeader
                    title="Immunisations"
                    icon="vaccine"
                    button={() => (
                        <Can ability="update" section="immunisations">
                            <Button
                                title="Add immunisation"
                                variant="primary"
                                size="lg"
                                href="/health-profile/immunisations/create"
                            />
                        </Can>
                    )}
                >
                    Document immunisations to keep them organised and accessible.
                </SectionHeader>
                <Only when={items.length}>
                    <FilteredList<ImmunisationListingItem>
                        data={items}
                        searchWith={(item, term) => item.title.toLowerCase().includes(term.toLowerCase())}
                        searchPlaceholder="Search by name"
                        refreshWith={refresh}
                        isRefreshing={refreshing}
                        renderItem={({ item }) => (
                            <ListingItem
                                title={item.title}
                                hideChevron
                                subtitle={item.date}
                                href={{ pathname: `${IMMUNISATION_BASE_URL}/[id]/show`, params: { id: item.id } }}
                                last
                            />
                        )}
                    />
                </Only>
            </Page>
        </Authorize>
    );
}
